import axios from "axios";
import { computed, ComputedRef, reactive } from "vue";

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import TimelineItemCollection from "../DTOs/TimelineItemCollection";
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from "./types";

export type TimelineItem = {
    datetime: string;
    total: number;
    unique: number;
    human: number;
};

export type TimelineInterval = "hour" | "day" | "week" | "month";
export type AnalyticsTimelineData = {
    dataset: TimelineItem[];
    interval: TimelineInterval;
    timezone: string;
};

export type iStatsTimelineAPI = {
    loading: ComputedRef<boolean>,
    setRouter: (router: HttpRouter<"get">) => iStatsTimelineAPI,
    fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
    fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
    fetchForTag: (args: FetchForTagArgs) => Promise<void>
    data: ComputedRef<Readonly<TimelineItemCollection>>
}

enum FakeDataType {
  DEFAULT = "default",
  RANDOM = "random",
};

function isFakeDataType(value: unknown): value is FakeDataType {
  return Object.values(FakeDataType).includes(value as FakeDataType);
}

const fakeData: (type?: FakeDataType | null) => TimelineItem[] = (type = FakeDataType.DEFAULT) => {
  if (type === 'default') {
    return [
      {
          datetime: "12-11-2025",
          total: 10,
          unique: 5,
          human: 8,
      },
      {
          datetime: "09-17-2025",
          total: 10,
          unique: 5,
          human: 8,
      },
      {
          datetime: "09-18-2025",
          total: 10,
          unique: 5,
          human: 2,
      },

      {
          datetime: "09-19-2025",
          total: 14,
          unique: 5,
          human: 9,
      },
      {
          datetime: "09-20-2025",
          total: 33,
          unique: 31,
          human: 1,
      },
    ];
  }

  return new Array(Math.round(Math.random() * 1000) + 100)
    .fill(null)
    .map(() => ({
      datetime: `${Math.round(Math.random() * 11) + 1}-${Math.round(Math.random() * 27) + 1}-2025`,
      total: Math.round(Math.random() * 1000) + 10,
      unique: Math.round(Math.random() * 100) + 10,
      human: Math.round(Math.random() * 100) + 10,
    }));
}

export default function StatsTimelineAPI(debug = false, type?: string | null): iStatsTimelineAPI {
    const state = reactive<{
        data: TimelineItemCollection,
        loading: boolean
    }>({
        data: new TimelineItemCollection([]),
        loading: false,
    });

    let router: HttpRouter<"get"> = axios;

    async function _fetch(url: string): Promise<void> {
        state.loading = true;

        if (debug) {
            const debugDataType: FakeDataType = isFakeDataType(type) ? type : FakeDataType.DEFAULT;
            state.data = new TimelineItemCollection(fakeData(debugDataType), "hour");
        } else {
            const response: HttpResponse<AnalyticsTimelineData> = await router.get(url);

            if (response.status !== 200) {
                throw new Error("Could not fetch timeline analytics data from " + url);
            }

            const result = response.data;
            state.data = new TimelineItemCollection(result.dataset, result.interval, result.timezone);
        }

        state.loading = false;
    }

    return {
        loading: computed(() => state.loading),
        data: computed(() => state.data),

        fetchForTeam: async function ({ orgID, teamID = null, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/timeline?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        fetchForAlias: async function ({ orgID, teamID = null, alias, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/timeline?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        fetchForTag: async function ({ orgID, teamID = null, tag, from, to, archived = false }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/timeline?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url);
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        },
    };
}
